import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiCheck, FiExternalLink } from "react-icons/fi";
import SEO from "../components/SEO";
import useReveal from "../hooks/useReveal";

const INK = "currentColor", TEAL = "#0d9488", MONO = "'JetBrains Mono', monospace";

// Real sample-report / drawing links (from product portal)
const SAMPLE_LINKS = [
  "https://drive.google.com/drive/folders/1zS0nOHAiP_JGnJ6iZkvgh3lunNto80QT",
  "https://drive.google.com/drive/folders/1lm8rJEVzCrRYWca-EHzXENcIPhmT87eE",
  "https://drive.google.com/drive/folders/1I_cbAw0JV0xuSPO44exUKWFZ2ufbLFUN",
  "https://pusulaerp.com.tr/",
];

const EtexTerminal = () => (
  <div className="term app-mock" style={{ marginTop: 0 }}>
    <div className="term-bar"><span className="d r" /><span className="d y" /><span className="d g" /><span className="ttl">etex — proje_A.edb</span></div>
    <div className="term-body">
      <div className="tl">$ etex --check tbdy2018 --model proje_A.edb</div>
      <div className="gh">[01] Loading model... <span className="hi">OK</span> (1.3s)</div>
      <div>&nbsp;&nbsp;stories: <span className="yel">14</span> · columns: <span className="yel">142</span> · beams: <span className="yel">487</span></div>
      <div className="gh">[02] Modal analysis...</div>
      <div>&nbsp;&nbsp;T₁ = <span className="hi">0.84s</span> · M₁ = <span className="yel">78.4%</span></div>
      <div className="gh">[03] Drift check (TBDY 4.9.1)...</div>
      <div>&nbsp;&nbsp;δ_i/h_i = <span className="ok">0.0072 ≤ 0.008 ✓</span></div>
      <div className="gh">[04] Capacity design (TS 500)...</div>
      <div>&nbsp;&nbsp;<span className="ok">98 OK</span> · <span className="err">2 over (B12, B14)</span></div>
      <div className="gh">[05] Export report...</div>
      <div className="hi">→ hesap_raporu.docx · kontroller.xlsx</div>
      <div className="ok">$ Done ✓</div>
    </div>
  </div>
);

const NetexVis = () => (
  <div className="svc-vis" style={{ minHeight: 380 }}>
    <div className="vis-bar"><span>netex.exe · Pushover & Mafsal</span><span>Performans</span></div>
    <div className="vis-stage">
      <svg viewBox="0 0 400 260" xmlns="http://www.w3.org/2000/svg">
        {/* capacity curve */}
        <polyline points="40,210 90,150 150,120 220,108 300,120 360,150" fill="none" stroke={TEAL} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="40" y1="40" x2="40" y2="220" stroke={INK} strokeWidth="1" opacity="0.5" />
        <line x1="40" y1="220" x2="370" y2="220" stroke={INK} strokeWidth="1" opacity="0.5" />
        {/* demand spectrum dashed */}
        <path d="M40,120 Q140,150 240,175 T360,200" fill="none" stroke="#f59e0b" strokeWidth="1.4" strokeDasharray="5 4" />
        {/* performance point */}
        <circle cx="220" cy="108" r="5" fill="#f59e0b" />
        <circle cx="220" cy="108" r="10" fill="none" stroke="#f59e0b" strokeWidth="1" opacity="0.5" />
        <g fontFamily={MONO} fontSize="8" fill="#8595a6">
          <text x="150" y="98">Performans noktası</text>
          <text x="300" y="240">Yerdeğiştirme →</text>
          <text x="46" y="52">Taban kesme</text>
        </g>
      </svg>
    </div>
    <div className="vis-foot"><div>IO<b>θp ≤ 0.005</b></div><div>LS<b>θp ≤ 0.025</b></div><div>CP<b>θp ≤ 0.040</b></div></div>
  </div>
);

const BetonVis = () => (
  <div className="svc-vis" style={{ minHeight: 380 }}>
    <div className="vis-bar"><span>Kolon donatı çizimi · Revit</span><span>C40</span></div>
    <div className="vis-stage">
      <svg viewBox="0 0 320 300" xmlns="http://www.w3.org/2000/svg">
        <rect x="90" y="60" width="140" height="180" fill="none" stroke={INK} strokeWidth="2" />
        <rect x="102" y="72" width="116" height="156" fill="none" stroke={TEAL} strokeWidth="1.2" />
        {/* longitudinal bars */}
        {[102, 160, 218].map((x) => [72, 150, 228].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="4.5" fill={TEAL} />))}
        {/* stirrup hooks */}
        <line x1="102" y1="72" x2="112" y2="62" stroke={TEAL} strokeWidth="1.2" />
        <line x1="218" y1="228" x2="208" y2="238" stroke={TEAL} strokeWidth="1.2" />
        <g fontFamily={MONO} fontSize="8" fill="#8595a6">
          <text x="90" y="52">S30 / 40×60</text>
          <text x="236" y="150">8ø20</text>
          <text x="236" y="164">ø10/10</text>
          <text x="90" y="260">Kalıp metrajı: otomatik</text>
        </g>
      </svg>
    </div>
    <div className="vis-foot"><div>Boyuna<b>8ø20</b></div><div>Etriye<b>ø10/10</b></div><div>Çıktı<b>Revit + DWG</b></div></div>
  </div>
);

const PusulaVis = () => (
  <div className="svc-vis" style={{ minHeight: 380 }}>
    <div className="vis-bar"><span>pusulaerp.com.tr · Dashboard</span><span>Bulut</span></div>
    <div className="vis-stage" style={{ display: "block", padding: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 14 }}>
        {[["Aktif proje", "24"], ["Bu ay hakediş", "₺1.8M"], ["Kapasite", "%86"]].map(([k, v]) => (
          <div key={k} style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px", background: "var(--bg)" }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.08em", color: "var(--slate-2)", textTransform: "uppercase" }}>{k}</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20, color: "var(--ink)", marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>
      <svg viewBox="0 0 400 120" style={{ width: "100%" }}>
        <g fill={TEAL}>
          {[30, 55, 40, 70, 60, 85, 75, 95].map((h, i) => (
            <rect key={i} x={12 + i * 48} y={110 - h} width="26" height={h} rx="2" opacity={0.55 + i * 0.05} />
          ))}
        </g>
        <line x1="0" y1="110" x2="400" y2="110" stroke={INK} strokeWidth="0.8" opacity="0.4" />
      </svg>
    </div>
    <div className="vis-foot"><div>Teklif<b>PDF</b></div><div>Timesheet<b>Onaylı</b></div><div>Finans<b>Hakediş</b></div></div>
  </div>
);

const VISUALS = [EtexTerminal, NetexVis, BetonVis, PusulaVis];

export default function Software() {
  const { t } = useTranslation();
  const pageRef = useRef(null);
  useReveal(pageRef, [t]);

  const products = t("software.products", { returnObjects: true });
  const pricing = t("software.pricing", { returnObjects: true });
  const tools = t("software.tools", { returnObjects: true });

  return (
    <div ref={pageRef}>
      <SEO title={t("software.meta_title")} description={t("software.meta_desc")} />

      <section className="page-hero blueprint">
        <div className="container ph-inner">
          <span className="eyebrow reveal">{t("software.hero.eyebrow")}</span>
          <h1 className="reveal d1">{t("software.hero.title_1")} <span className="accent">{t("software.hero.title_hl")}</span></h1>
          <p className="ph-lede reveal d2">{t("software.hero.lede")}</p>
        </div>
      </section>

      {/* PRODUCTS */}
      {products.map((prod, i) => {
        const Vis = VISUALS[i];
        const flip = i % 2 === 1;
        const nm = prod.name.toLowerCase();
        const secId = nm.includes("beton") ? "beton" : nm.includes("pusula") ? "pusula" : nm;
        return (
          <section className={`section${i % 2 ? " soft" : ""}`} id={secId} key={i}>
            <div className="container">
              <div className="product">
                {flip && <div className="reveal d1" style={{ order: -1 }}><Vis /></div>}
                <div className="pb-meta">
                  <div className="pb-tag reveal">{prod.tag}</div>
                  <div className="pb-pills reveal d1">{prod.pills.map((pl, j) => <span className={`pill${j === 0 ? " teal" : ""}`} key={j}>{pl}</span>)}</div>
                  <h2 className="reveal d1">{prod.name}<span className="accent">.</span></h2>
                  <p className="pb-tagline reveal d2">{prod.tagline}</p>
                  <div className="pb-specs reveal d2">
                    {prod.specs.map((s, j) => <div key={j}><span>{s.k}</span><b>{s.v}</b></div>)}
                  </div>
                  <div className="pb-actions reveal d3">
                    {prod.external
                      ? <a href={prod.to} target="_blank" rel="noopener noreferrer" className="btn">{prod.cta} <FiExternalLink /></a>
                      : <Link to={prod.to} className="btn">{prod.cta} <FiArrowRight className="arrow" /></Link>}
                    <a href={SAMPLE_LINKS[i]} target="_blank" rel="noopener noreferrer" className="btn ghost">{prod.cta2} <FiExternalLink /></a>
                  </div>
                </div>
                {!flip && <div className="reveal d1"><Vis /></div>}
              </div>
            </div>
          </section>
        );
      })}

      {/* PRICING */}
      <section className="section mute" id="fiyatlandirma">
        <div className="container">
          <span className="eyebrow reveal">{pricing.eyebrow}</span>
          <h2 className="h-lg reveal d1" style={{ margin: "14px 0 12px" }}>{pricing.title}</h2>
          <p className="muted reveal d2" style={{ maxWidth: 620 }}>{pricing.intro}</p>
          <div className="pricing" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            {pricing.plans.map((plan, i) => (
              <div className={`price-card reveal${plan.featured ? " feat" : ""}`} style={{ transitionDelay: `${(i % 3) * 0.05}s` }} key={i}>
                <div className="ptag">{plan.tag}</div>
                <div className="pname" style={{ fontSize: 24 }}>{plan.name}</div>
                <div className="pamt" style={{ fontSize: 34, marginTop: 14 }}>{plan.price}</div>
                <div className="pper">{plan.per}</div>
                {plan.note && <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--teal-700)", marginTop: 6 }}>↓ {plan.note}</div>}
                <ul>{plan.features.map((f, j) => <li key={j}><FiCheck /> {f}</li>)}</ul>
                <Link to="/iletisim" className={`btn${plan.featured ? "" : " ghost"}`}>{pricing.buy} <FiArrowRight className="arrow" /></Link>
              </div>
            ))}
          </div>
          <div className="reveal" style={{ marginTop: 24, padding: 20, border: "1px solid var(--line)", borderRadius: "var(--r)", textAlign: "center", color: "var(--slate)", fontSize: 14 }}>
            {pricing.foot}
          </div>
        </div>
      </section>

      {/* WEB TOOLS */}
      <section className="section" id="araclar">
        <div className="container">
          <span className="eyebrow reveal">{tools.eyebrow}</span>
          <h2 className="h-lg reveal d1" style={{ margin: "14px 0 12px" }}>{tools.title}</h2>
          <p className="muted reveal d2" style={{ maxWidth: 560 }}>{tools.intro}</p>
          <div className="tools-grid">
            {tools.items.map((tl, i) => {
              const inner = (
                <>
                  <div className="tc-top">
                    <span className="tc-ic">{tl.icon}</span>
                    {tl.active ? <span className="tc-num">{tools.open} →</span> : <span className="pill soon">{tools.soon}</span>}
                  </div>
                  <div style={{ flex: 1 }}><h4>{tl.name}</h4><p>{tl.desc}</p></div>
                </>
              );
              return tl.active ? (
                <Link to={tl.to} className="tool-card reveal" style={{ transitionDelay: `${(i % 3) * 0.05}s` }} key={i}>{inner}</Link>
              ) : (
                <div className="tool-card soon reveal" style={{ transitionDelay: `${(i % 3) * 0.05}s` }} key={i}>{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOM DEV */}
      <section className="section soft">
        <div className="container">
          <div className="approach-grid" style={{ alignItems: "center" }}>
            <div>
              <h2 className="h-lg reveal">{t("software.custom.title_1")} <span className="accent">{t("software.custom.title_hl")}</span></h2>
            </div>
            <div className="reveal d1">
              <p className="muted" style={{ marginBottom: 22, fontSize: 16, lineHeight: 1.65 }}>{t("software.custom.desc")}</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Link to="/iletisim" className="btn">{t("software.custom.cta")} <FiArrowRight className="arrow" /></Link>
                <Link to="/hizmetler#yazilim" className="btn ghost">{t("software.custom.cta2")}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
